import type { AssignProjectData } from 'components'
import { modals, notifications } from 'context'
import { useCallback, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useQueryClient } from 'react-query'
import { useNavigate } from 'react-router-dom'
import { deleteProject } from 'api'
import { assignProject, assignPublishedTemplate, completeProject } from '../../services/arcProjectsApi'
import type { ArcProject } from '../../types'
import { Text } from 'components'

interface UseProjectCardHandlersOptions {
  project: ArcProject
  isLibrary: boolean
  backlink?: string
}

export const useProjectCardHandlers = ({ project, isLibrary, backlink }: UseProjectCardHandlersOptions) => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const queryClient = useQueryClient()

  const [assignModalOpened, setAssignModalOpened] = useState(false)
  const [editStudentsOpened, setEditStudentsOpened] = useState(false)
  const [deleteModalOpened, setDeleteModalOpened] = useState(false)
  const [shareModalOpened, setShareModalOpened] = useState(false)
  const [isAssigning, setIsAssigning] = useState(false)

  const buildProjectUrl = useCallback((projectId: string) => (
    backlink
      ? `/projects/${projectId}?back=${encodeURIComponent(backlink)}`
      : `/projects/${projectId}`
  ), [backlink])

  const projectUrl = buildProjectUrl(project.id)

  const handleView = useCallback(() => navigate(projectUrl), [navigate, projectUrl])
  const handleEdit = useCallback(() => navigate(`/projects/${project.id}/edit`), [navigate, project.id])

  const openAssignModal = useCallback(() => setAssignModalOpened(true), [])
  const closeAssignModal = useCallback(() => {
    if (!isAssigning) setAssignModalOpened(false)
  }, [isAssigning])

  const openDeleteModal = useCallback(() => setDeleteModalOpened(true), [])
  const closeDeleteModal = useCallback(() => setDeleteModalOpened(false), [])

  const closeEditStudents = useCallback(() => {
    setEditStudentsOpened(false)
    queryClient.invalidateQueries(['arc-projects'])
  }, [queryClient])

  const handleAssignSave = useCallback(async (data: AssignProjectData) => {
    if (!data.groupId || data.assignedStudentIds.length === 0) {
      notifications.show({
        title: t('general_error_title'),
        message: t('arc_projects.assign_validation_error'),
        color: 'red',
      })
      return
    }

    setIsAssigning(true)

    try {
      const response = isLibrary
        ? await assignPublishedTemplate(project.id)
        : await assignProject(project.id, data.groupId, data.assignedStudentIds, data.startDate)

      if (response.success) {
        const assignedProjectId = 'projectId' in response && response.projectId
          ? response.projectId
          : 'project' in response && response.project?.id
            ? response.project.id
            : undefined

        if (assignedProjectId) {
          notifications.show({ message: t('arc_projects.assign_success'), color: 'green' })
          queryClient.invalidateQueries(['arc-projects'])
          setAssignModalOpened(false)
          navigate(buildProjectUrl(assignedProjectId))
        } else {
          notifications.show({ title: t('general_error_title'), message: t('arc_projects.assign_error'), color: 'red' })
        }
      } else {
        const errorMessage = response.error === 'UNAUTHORIZED'
          ? t('arc_projects.assign_unauthorized')
          : response.error || t('arc_projects.assign_error')
        notifications.show({ title: t('general_error_title'), message: errorMessage, color: 'red' })
      }
    } catch (error: unknown) {
      const axiosError = error as { response?: { data?: { error?: string; message?: string } } }
      const errorMessage = axiosError.response?.data?.error
        || axiosError.response?.data?.message
        || t('arc_projects.assign_error')
      notifications.show({ title: t('general_error_title'), message: errorMessage, color: 'red' })
    } finally {
      setIsAssigning(false)
    }
  }, [project.id, isLibrary, t, queryClient, navigate, buildProjectUrl])

  const handleDeleteConfirm = useCallback(() => {
    deleteProject(project.id)
      .then(response => {
        if (response.status === 200) {
          notifications.show({ message: t('new_project_view.message_project_removed'), color: 'green' })
          queryClient.invalidateQueries(['arc-projects'])
        }
      })
      .catch(error => {
        notifications.show({
          title: t('general_error_title'),
          message: error instanceof Error ? error.message : t('general_error_message'),
          color: 'red',
        })
      })
  }, [project.id, t, queryClient])

  const handleComplete = useCallback(() => {
    modals.openConfirmModal({
      title: t('new_project_view.finish_confirmation_title'),
      children: <Text size='md'>{t('new_project_view.finish_confirmation_description')}</Text>,
      labels: { confirm: t('general_buttons_confirm'), cancel: t('general_buttons_cancel') },
      confirmProps: { size: 'lg' },
      cancelProps: { size: 'lg' },
      onCancel: () => {},
      onConfirm: async () => {
        try {
          const result = await completeProject(project.id)
          if (result.success) {
            notifications.show({ message: t('new_project_view.message_project_finished'), color: 'green' })
            queryClient.invalidateQueries(['arc-projects'])
          } else {
            const errorMessage = result.error === 'UNAUTHORIZED'
              ? t('general_error_message_unauthorized')
              : (result.error || t('general_error_message'))
            notifications.show({ title: t('general_error_title'), message: errorMessage, color: 'red' })
          }
        } catch (error) {
          notifications.show({
            title: t('general_error_title'),
            message: error instanceof Error ? error.message : t('general_error_message'),
            color: 'red',
          })
        }
      },
    })
  }, [project.id, t, queryClient])

  return {
    assignModalOpened, setAssignModalOpened, openAssignModal, closeAssignModal,
    editStudentsOpened, setEditStudentsOpened, closeEditStudents,
    deleteModalOpened, openDeleteModal, closeDeleteModal,
    shareModalOpened, setShareModalOpened,
    isAssigning,
    projectUrl,
    handleView, handleEdit,
    handleAssignSave, handleDeleteConfirm, handleComplete,
  }
}
